import { defineComponent, ref, computed, reactive, toRef } from 'vue-demi';
import { a as useIdAttribute, u as useControlAttributes } from './useIdAttribute-859439f0.js';
import '@vueuse/core';
import { u as useProxyModelValue } from './useProxyModelValue-940010d2.js';
import '@onereach/styles/tailwind.config';
import 'tailwindcss/resolveConfig';
import { s as script$1, L as LabelVariant } from './OrLabel-4da56db0.js';
import { resolveComponent, openBlock, createElementBlock, normalizeClass, createElementVNode, mergeProps, createBlock, withCtx, renderSlot, createTextVNode, toDisplayString, createCommentVNode } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

function isEmptyValue(val) {
  return val === undefined || val === null || val === '';
}

const CheckboxRoot = [
// Layout
'layout-row',
// Spacing
'gap-sm+ md:gap-sm'];
const CheckboxControl = [
// Layout
'layout-inline-row justify-center',
// Box
'w-md+ md:w-md', 'h-md+ md:h-md',
// Shape
'rounded-sm',
// Interactivity
'interactivity-auto',
// Iconography
'iconography-outlined',
// Theme
'theme-preset-3-primary', 'dark:theme-preset-3-primary-dark', 'theme-background-default', 'dark:theme-background-default-dark', 'theme-border-1-outline', 'dark:theme-border-1-outline-dark',
// Theme (focus)
'focus:theme-outline-2-primary', 'dark:focus:theme-outline-2-primary-dark',
// Theme (checked)
'checked:theme-preset-1-primary', 'dark:checked:theme-preset-1-primary-dark',
// Theme (indeterminate)
'indeterminate:theme-preset-1-primary', 'dark:indeterminate:theme-preset-1-primary-dark'];
const CheckboxControlHandle = [
// Content (checked)
'checked:after:content-checkbox-checked',
// Content (indeterminate)
'indeterminate:after:content-checkbox-indeterminate'];

var script = defineComponent({
  name: 'OrCheckbox',
  components: {
    OrLabel: script$1
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue'
  },
  props: {
    modelValue: {
      type: null,
      default: null
    },
    value: {
      type: [Object, String, Number, Boolean],
      default: null
    },
    label: {
      type: String,
      default: undefined
    },
    labelVariant: {
      type: String,
      default: LabelVariant.Toggle
    },
    indeterminate: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'click', 'focus', 'blur'],
  expose: ['root', 'focus', 'blur'],
  setup(props, context) {
    // Refs
    const root = ref();
    const control = ref();
    // Styles
    const rootStyles = computed(() => ['or-checkbox-v3', ...CheckboxRoot]);
    const controlStyles = computed(() => [...CheckboxControl, ...CheckboxControlHandle]);
    // State
    const controlAttributes = reactive({
      id: useIdAttribute(),
      ...useControlAttributes()
    });
    const proxyModelValue = useProxyModelValue(toRef(props, 'modelValue'), context.emit);
    const isChecked = computed(() => {
      if (Array.isArray(proxyModelValue.value)) {
        return !isEmptyValue(props.value) && proxyModelValue.value.includes(props.value);
      }
      return !!proxyModelValue.value;
    });
    // Methods
    function handleChange() {
      if (Array.isArray(proxyModelValue.value)) {
        if (isChecked.value) {
          proxyModelValue.value = proxyModelValue.value.filter(value => value !== props.value);
        } else {
          proxyModelValue.value = [...proxyModelValue.value, props.value];
        }
      } else {
        proxyModelValue.value = !proxyModelValue.value;
      }
    }
    function focus() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.focus();
    }
    function blur() {
      var _a;
      (_a = control.value) === null || _a === void 0 ? void 0 : _a.blur();
    }
    return {
      root,
      control,
      rootStyles,
      controlStyles,
      controlAttributes,
      isChecked,
      handleChange,
      focus,
      blur
    };
  }
});

const _hoisted_1 = ["checked", ".indeterminate", "required", "disabled"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrLabel = resolveComponent("OrLabel");
  return openBlock(), createElementBlock("div", {
    ref: "root",
    class: normalizeClass(_ctx.rootStyles)
  }, [createElementVNode("input", mergeProps({
    ref: "control",
    checked: _ctx.isChecked
  }, _ctx.controlAttributes, {
    class: _ctx.controlStyles,
    type: 'checkbox',
    ".indeterminate": _ctx.indeterminate,
    required: _ctx.required,
    disabled: _ctx.disabled,
    onChange: _cache[0] || (_cache[0] = function () {
      return _ctx.handleChange && _ctx.handleChange(...arguments);
    }),
    onClick: _cache[1] || (_cache[1] = $event => _ctx.$emit('click', $event)),
    onFocus: _cache[2] || (_cache[2] = $event => _ctx.$emit('focus', $event)),
    onBlur: _cache[3] || (_cache[3] = $event => _ctx.$emit('blur', $event))
  }), null, 16 /* FULL_PROPS */, _hoisted_1), _ctx.$slots.default || _ctx.label ? (openBlock(), createBlock(_component_OrLabel, {
    key: 0,
    "control-id": _ctx.controlAttributes.id,
    variant: _ctx.labelVariant,
    required: _ctx.required,
    disabled: _ctx.disabled
  }, {
    addon: withCtx(() => [renderSlot(_ctx.$slots, "addon")]),
    default: withCtx(() => [renderSlot(_ctx.$slots, "default", {}, () => [createTextVNode(toDisplayString(_ctx.label), 1 /* TEXT */)])]),

    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["control-id", "variant", "required", "disabled"])) : createCommentVNode("v-if", true)], 2 /* CLASS */);
}

var css_248z = "\ninput[data-v-5358d710] {\n  appearance: none;\n  line-height: 0;\n}\n";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-5358d710";
script.__file = "src/components/or-checkbox-v3/OrCheckbox.vue";

export { isEmptyValue as i, script as s };
